import { Box } from '@mui/material';
import { useActivatePatient } from '_api';
import { useGetAllArchivedPatients } from '_api/queries';
import { Patient } from '_api/types';
import { ALERT_TYPE, PatientRouteMap, alertPriority } from '_constants';
import { ActionsFlexBox } from 'components/molecules';
import { TableHeader } from 'components/organisms';
import dayjs from 'dayjs';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import React, { useEffect, useMemo, useState } from 'react';
import { FaCircleQuestion } from 'react-icons/fa6';
import { useNavigateTo } from 'utils';
import { usePatients, useSearchResults, useUser } from 'utils/hooks';

import { PatientsManagementProps } from './types';

export const PatientsManagement = ({ archived = false }: PatientsManagementProps) => {
  const { existingSearchTerm, updateSearchTerm } = useSearchResults();
  const navigate = useNavigateTo();
  const { mutateAsync: activatePatient } = useActivatePatient();
  const { activePatients, allPatients, deletePatient, isLoading, therapistPatients } =
    usePatients({ withAlerts: true });
  const { data: archivedPatients } = useGetAllArchivedPatients({});
  const { isAdmin } = useUser();
  const [searchTerm, setSearchTerm] = useState(existingSearchTerm || '');
  const filteredData = useMemo<Patient[]>(() => {
    const patientList =
      !isAdmin && therapistPatients
        ? therapistPatients
        : archived && allPatients
          ? archivedPatients
          : activePatients;
    if (searchTerm.length > 0 && patientList) {
      const lowercasedSearchTerm = searchTerm.toLowerCase();
      const searchPatients =
        (searchTerm: string) =>
        ({ firstName, lastName, id, mrn }: Patient) =>
          `${firstName} ${lastName}`.toLowerCase().includes(searchTerm) ||
          id.toString().includes(searchTerm) ||
          mrn?.toLowerCase().includes(searchTerm);

      const filtered = patientList.filter(searchPatients(lowercasedSearchTerm));

      return filtered;
    } else {
      return patientList || [];
    }
  }, [searchTerm, activePatients, allPatients]);

  useEffect(() => setSearchTerm(existingSearchTerm || ''), [existingSearchTerm]);

  const toCreatePatient = () => navigate(PatientRouteMap.ADD);

  const handleArchiveOnClick = (patientId: number) => deletePatient({ patientId });

  const handleActivateOnClick = (patientId: number) => {
    activatePatient({ patientId });
  };

  const handleViewOnClick = (id: number) => {
    navigate(PatientRouteMap.DETAIL, id.toString());
  };

  const handleEditOnClick = (id: number) => {
    navigate(PatientRouteMap.EDIT, id.toString());
  };

  const patientsTableColumns = useMemo<MRT_ColumnDef<Patient>[]>(
    () => [
      {
        header: 'Alert',
        enableSorting: true,
        size: 10,
        accessorFn: (row) =>
          row.alertType &&
          row.alertType !== null &&
          row.showAlerts &&
          row.showAlerts === true ? (
            row.alertType === ALERT_TYPE.MISSING ? (
              <FaCircleQuestion
                style={{ color: 'gray', height: '15%', margin: 'auto', width: '30%' }}
              />
            ) : (
              <svg
                width="30"
                height="30"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill={row.alertType === ALERT_TYPE.RED ? '#DB1823' : '#E6B55E'}
                  d="M12 2L2 22h20L12 2z"
                />
                <path
                  fill={row.alertType === ALERT_TYPE.RED ? '#FFFFFF' : '#050000'}
                  d="M11 14v-5h2v5h-2zm0 3v-2h2v2h-2z"
                />
              </svg>
            )
          ) : null,
        sortingFn: (rowA, rowB) => {
          const ra = rowA.original;
          const rb = rowB.original;

          return alertPriority(ra.alertType) - alertPriority(rb.alertType);
        },
      },
      {
        header: 'Patient Id',
        enableSorting: false,
        accessorFn: (row) => row.id,
        size: 10,
      },
      {
        header: 'MRN',
        size: 10,
        accessorFn: (row) => `${row?.mrn}`,
      },
      {
        header: 'Child',
        accessorFn: (row) => `${row.lastName}, ${row.firstName} `,
      },
      {
        header: 'Therapist',
        accessorFn: (row) => {
          return row.therapists.map(
            (therapist, i) =>
              `${therapist.firstName} ${therapist.lastName}${i < row.therapists.length - 1 ? ', ' : ''}`,
          );
        },
      },
      {
        header: 'Activity',
        accessorFn: (row) => {
          return `${row.lastActivityDate ? dayjs(row.lastActivityDate).format('MM/DD/YYYY') : 'N/A'}`;
        },
        sortingFn: (rowA, rowB, _columnId) => {
          return (rowA.original.lastActivityDate || '').localeCompare(
            rowB.original.lastActivityDate || '',
          );
        },
      },
      {
        header: 'Actions',
        enableSorting: false,
        accessorFn: (data) => (
          <ActionsFlexBox
            archiveOnClick={() => handleArchiveOnClick(data.id)}
            editOnClick={() => handleEditOnClick(data.id)}
            viewOnClick={() => handleViewOnClick(data.id)}
            activateOnClick={() => handleActivateOnClick(data.id)}
            actionButtons={{
              viewButton: archived ? false : true,
              editButton: archived ? false : true,
              archiveButton: archived ? false : true,
              activateButton: archived ? true : false,
            }}
          />
        ),
        size: 10,
      },
    ],
    [],
  );

  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
    updateSearchTerm(searchTerm);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <TableHeader
          disableButton={archived}
          tooltip={"Can only create Patients in the 'Active' table view"}
          onAddClick={toCreatePatient}
          handleSearch={handleSearch}
          searchPlaceholder={'Search My Patients'}
          value={searchTerm}
        />
      </Box>
      <Box sx={{ marginTop: 10 }}>
        {filteredData && !isLoading ? (
          <MaterialReactTable
            columns={patientsTableColumns}
            data={filteredData}
            positionActionsColumn="last"
            enableSorting={true}
            enableColumnActions={false}
            enableTopToolbar={false}
            enablePagination={true}
          />
        ) : null}
      </Box>
    </Box>
  );
};
